import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useDispatch } from 'react-redux';
import { openCustomDrawer } from '../../../redux/slices/modalSlice';
import { fetchAllDriversLocations, fetchLiveDriversLocations } from '../../../redux/slices/hyperdrive/drivers';
import { useSelector } from 'react-redux';
import { formatTimestampToMapOld, formatTimestampTo12HourTimeUtc, getUniqueByLatLng } from 'utilities/common';
// Import all your marker icons

import notStartedMarker from '../../../assets/images/icons/marker/yellowmarker.svg';
import driverIdle from '../../../assets/images/icons/driversMarker/driver-idle.svg';
import driverOffDuty from '../../../assets/images/icons/driversMarker/off-duty.svg';
import onBreak from '../../../assets/images/break.svg';
import outOfService from '../../../assets/images/outOfService.svg';
import flagIcon from '../../../assets/images/driveFlag.svg';
import onTransit from '../../../assets/images/inProgress.svg';
import arrowIcon from '../../../assets/images/arrow-line-4.png';
import { useSearchParams } from 'react-router-dom';
import { updateDriverStatusApi } from '../../../redux/slices/hyperdrive/setting';
import moment from 'moment';
import { formatTimeTo12Hour } from 'utilities/common';
import { getIconbyStatus } from 'utilities/common';
mapboxgl.accessToken = process.env.REACT_APP_MAP_KEY_TOKEN;
import clusterMarker from '../../../assets/images/icons/marker/greymark.svg';


const MapDrive = ({ dataPassComponent, driverZoomOnMapRef = {}, testingPanel, handleTaskClick = () => { }, selectedTaskId }) => {
  const dispatch = useDispatch();
  const { driversLocation = {}, loading = false, liveDriverLocations = {}, isFetchingLiveDrivers = false } = useSelector(state => state.drivers);
  // const { loading = false, isFetchingLiveDrivers = false } = useSelector(state => state.drivers);

  // Refs
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const driverMarkersRef = useRef([]); // Separate ref for driver markers
  const taskMarkersRef = useRef([]);   // Separate ref for task markers, now unused but kept if needed for cleanup
  const taskDataMapRef = useRef({});   // Store task data by ID
  const markersOnScreen = useRef({});  // Store active markers by ID
  const popupRef = useRef(null);
  const [taskDetailFetch, setTaskDetailFetch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsRef = useRef(searchParams);

  useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);

  const fullRoleTablat = useMemo(() => searchParams.get('lat'), [searchParams]);
  const fullRoleTablng = useMemo(() => searchParams.get('lng'), [searchParams]);
  const fullRoleTabzomm = useMemo(() => searchParams.get('zoom'), [searchParams]);
  const taskStatus = useMemo(() => searchParams.get('taskStatus'), [searchParams]);
  const getTimeRangeParam = useMemo(() => searchParams.get('timeRange'), [searchParams]);
  const driverStatus = useMemo(() => searchParams.get('driverStatus'), [searchParams]);

  const [formData, setFormData] = useState({
    taskStatus: ['not_started', 'in_progress', 'completed', 'cancelled', 'unassigned'],
    driverStatus: ['idle', 'intransit', 'onbreak', 'outOfService']
  });
  const today = new Date();
  const nextDate = moment(today).add(1, 'day').toDate();
  const [timeRange, setTimeRange] = useState({
    startDate: today,
    endDate: nextDate,
  });
  // Constants
  const INITIAL_CENTER = [fullRoleTablng ? fullRoleTablng : -118.2426, fullRoleTablat ? fullRoleTablat : 34.0549];
  const INITIAL_ZOOM = fullRoleTabzomm ? fullRoleTabzomm : 9.5;

  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;

    const updateCenter = () => {
    };

    map.on('moveend', () => {
      const center = map.getCenter();
      const zoom = map.getZoom();
      const currentParams = Object.fromEntries(searchParamsRef.current);

      setSearchParams({
        ...currentParams,
        roleTab: 'map',
        lat: center.lat,
        lng: center.lng,
        zoom: zoom,
      });
    });
    map.on('load', updateCenter);
    map.on('moveend', updateCenter);

    return () => {
      map.off('load', updateCenter);
      map.off('moveend', updateCenter);
    };
  }, [mapRef.current]);


  // Memoized driver locations
  const memoizeDriversLocation = useMemo(() => {
    const locations = liveDriverLocations?.length > 0 ? liveDriverLocations?.map((driver => {
      return {
        lat: driver.latitude || 33.8254462,
        lng: driver.longitude || -117.5636722,
        status: driver?.driverTaskStatus === 'in_progress' ? '1' : driver?.driverTaskStatus === 'offduty' ? '2' : driver?.driverTaskStatus === 'onBreak' ? '4' : driver?.driverTaskStatus === 'outOfService' ? '5' : '3',
        fleetId: driver.fleetId,
        driverName: driver.fleetName,
        fleetVehicleDetails: driver?.fleetVehicleDetails,
        taskName: driver?.taskName,
        driverTaskStatus: driver?.driverTaskStatus,
        fromTime: driver?.fromTime,
        toTime: driver?.toTime,
      };
    })) || [] : [];
    return locations;
  }, [liveDriverLocations]);

  // Memoized driver task locations

  const memoizeDriversTasksLocation = useMemo(() => {
    const taskLists = [];
    driversLocation?.data?.forEach(driver => {
      driver?.tasks?.forEach(task => {
        taskLists.push({
          lat: task?.address?.latitude || '',
          lng: task?.address?.longitude || '',
          status: task?.taskStatus,
          taskType: task?.taskType,
          fleetId: task?.fleetId,
          fleetName: task?.fleet?.fleetName,
          address: task?.address,
          taskEndTime: task?.taskEndTime,
          taskStartTime: task?.taskStartTime,
          taskName: task?.taskName,
          fleet: task?.fleet,
          taskType: task?.taskType,
          recipientDetails: task?.recipientDetails,
          actualTaskStartTime: task?.actualTaskStartTime,
          actualTaskEndTime: task?.actualTaskEndTime,
          orderType: task?.orderType || '-',
          _id: task?._id,
          fromTime: task?.fromTime,
          toTime: task?.toTime,
          taskDisplayId: task?.taskDisplayId,
          slotDetails: task?.slotDetails || {},
          coreRegion: task?.coreRegion || '-',
          // breakAddress: data?.tasks
        });
      });
    });
    return taskLists;
  }, [driversLocation?.data]);

  // Initialize the map
  useEffect(() => {
    if (!mapContainerRef.current) return;

    // FIX: Ensure map container itself is at the bottom
    mapContainerRef.current.style.zIndex = '0';

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/dark-v10',
      center: INITIAL_CENTER,
      zoom: INITIAL_ZOOM,
      // --------------------2D----------------------
      pitchWithRotate: false,      // Disable pitch with rotation
      dragRotate: false,           // Disable rotation by dragging
      touchZoomRotate: false,      // Disable rotation on touch devices
      maxPitch: 0,                 // Set maximum pitch to 0 (completely flat)
      projection: 'mercator'       // Use standard 2D projection
    });

    mapRef.current = map;


    // Add navigation controls
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');

    map.on('load', () => {
      // Add source for clustering
      map.addSource('task-locations', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        cluster: true,
        clusterMaxZoom: 14, // Max zoom to cluster points on
        clusterRadius: 50, // Radius of each cluster when clustering points (defaults to 50)
        clusterProperties: {
          // Sum the 'count' property from the leaves
          'point_count_sum': ['+', ['get', 'count']]
        }
      });

      // Add invisible layers to query against
      map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'task-locations',
        filter: ['has', 'point_count'],
        paint: { 'circle-opacity': 0 }
      });

      map.addLayer({
        id: 'unclustered-point',
        type: 'circle',
        source: 'task-locations',
        filter: ['!', ['has', 'point_count']],
        paint: { 'circle-opacity': 0 }
      });

      updateMarkers();
    });

    map.on('move', updateMarkers);
    map.on('moveend', updateMarkers);
    // Also update when the source data changes
    map.on('sourcedata', (e) => {
      if (e.sourceId === 'task-locations' && map.isSourceLoaded('task-locations') && e.isSourceLoaded) {
        updateMarkers();
      }
    });

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);



  // Zoom to driver function
  useEffect(() => {
    if (!driverZoomOnMapRef.current) return;

    driverZoomOnMapRef.current.zoomOnDriver = (driverInfo) => {

      const { latitude: lat, longitude: lng } = driverInfo || {};
      mapRef.current.flyTo({
        center: [lng, lat],
        zoom: 10
      });
      if (driverInfo?.tasksInfo === 'tasksInfo') {
        showTaskInfo(driverInfo?.tasks, [lng, lat]);
      }
      else {

        // const inProgressTasks = driverInfo.tasks?.filter(t =>
        //   t.taskType === 'deliveryTask' && t.taskStatus === 'in_progress') || [];
        // const notStartedTasks = driverInfo.tasks?.filter(t =>
        //   t.taskType === 'deliveryTask' && t.taskStatus === 'not_started') || [];
        // const returnToHQ = driverInfo.tasks?.filter(t =>
        //   t.taskStatus === 'returnToHeadquarterTask') || [];

        // const inProgressStartTasks = driverInfo.tasks?.filter(t =>
        //   t.taskType === 'startTask' && t.taskStatus === 'in_progress') || [];

        // const notStartedStartTasks = driverInfo.tasks?.filter(t =>
        //   t.taskType === 'startTask' && t.taskStatus === 'not_started') || [];

        // const combined = [...inProgressTasks, ...notStartedTasks, ...returnToHQ, ...inProgressStartTasks, ...notStartedStartTasks];
        // const uniqueTasks = getUniqueByLatLng(combined);

        // if (uniqueTasks.length > 0) {
        //   const taskCoords = uniqueTasks.map(task => [
        //     Number(task?.address?.longitude),
        //     Number(task?.address?.latitude)
        //   ]);

        //   const startPoint = [driverInfo.longitude, driverInfo.latitude];

        //   drawMultipleStraightLines(startPoint, taskCoords);
        // }

        showDriverInfo({
          driverName: driverInfo?.driverName,
          lat,
          lng,
          taskName: driverInfo?.taskName,
          driverTaskStatus: driverInfo?.taskStatus,
          fromTime: driverInfo?.fromTime,
          toTime: driverInfo?.toTime,
        });
      }
    };
  }, []);
  const removeRouteFromMap = () => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    // Remove layers in reverse order of creation
    if (map.getLayer('route-arrows')) {
      map.removeLayer('route-arrows');
    }

    if (map.getLayer('route')) {
      map.removeLayer('route');
    }

    // Remove arrow source if it exists
    if (map.getSource('route-arrows')) {
      map.removeSource('route-arrows');
    }

    // Remove route source
    if (map.getSource('route')) {
      map.removeSource('route');
    }

    // Remove custom image
    if (map.hasImage('arrow-icon')) {
      map.removeImage('arrow-icon');
    }
  };



  const removeDriverMarkers = () => {
    driverMarkersRef.current.forEach(marker => {
      marker.remove();
      const el = marker.getElement();
      if (el) el.remove();
    });
    driverMarkersRef.current = [];
  };

  // **********************************************newwwwwwwwwwwwwww*********************************************
  const removeTaskMarkers = useCallback(() => {
    // This is now handled within updateMarkers logic more granularly for performance,
    // but kept for full cleanup if needed.
    taskMarkersRef.current.forEach(id => {
      // Since we are tracking IDs in a Map or Object now, this reference might need change.
      // However, we will fundamentally change how taskMarkersRef is used.
      // It seems taskMarkersRef was an array of markers.
      // We will repurpose it to be a Map { id: marker }.
    });

    // For now, let's just use it to clear ALL if needed, but the new logic will manage state better.
    // We'll reimplement the reference management inside the component scope slightly differently.
  }, []);


  const removePopup = () => {
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }
  };

  const showDriverLocations = useCallback(() => {
    if (!mapRef.current) return;

    // Check if driver data  changed
    const currentDriverIds = driverMarkersRef.current.map(m => m._driverId);
    const newDriverIds = memoizeDriversLocation.map(d => d.fleetId);

    if (JSON.stringify(currentDriverIds.sort()) === JSON.stringify(newDriverIds.sort())) {
      return; // No change in drivers
    }

    removeDriverMarkers();

    memoizeDriversLocation.forEach(driver => {
      if (!driver.lat || !driver.lng) return;

      let iconUrl;
      switch (driver.status) {
        case '1': iconUrl = onTransit; break;
        case '2': iconUrl = driverOffDuty; break;
        case '4': iconUrl = onBreak; break;
        case '5': iconUrl = outOfService; break;

        default: iconUrl = driverIdle;
      }

      const el = document.createElement('div');
      el.className = 'driver-marker';
      el.style.backgroundImage = `url(${iconUrl})`;
      el.style.width = '16px';
      el.style.height = '16px';
      el.style.backgroundSize = 'contain';
      el.style.cursor = 'pointer';

      const marker = new mapboxgl.Marker(el, { draggable: driver.status !== '4' })
        .setLngLat([driver.lng, driver.lat])
        .addTo(mapRef.current);

      // Log the driver object and lat/lng when dragging ends
      marker.on("dragend", () => {
        const { lng, lat } = marker.getLngLat();
        updateDriverLocation({ lat, lng, driver })
      });

      const driverData = {
        ...driver,
        element: el
      };

      el.addEventListener('mouseenter', () => {
        const driverTasks = driversLocation?.data?.find(d => d.fleetId === driverData.fleetId);

        if (!driverTasks) return;

        const inProgressTasks = driverTasks.tasks?.filter(t =>
          t.taskType === 'deliveryTask' && t.taskStatus === 'in_progress') || [];
        const notStartedTasks = driverTasks.tasks?.filter(t =>
          t.taskType === 'deliveryTask' && t.taskStatus === 'not_started') || [];
        const returnToHQ = driverTasks.tasks?.filter(t =>
          t.taskStatus === 'returnToHeadquarterTask') || [];

        const inProgressStartTasks = driverTasks.tasks?.filter(t =>
          t.taskType === 'startTask' && t.taskStatus === 'in_progress') || [];

        const notStartedStartTasks = driverTasks.tasks?.filter(t =>
          t.taskType === 'startTask' && t.taskStatus === 'not_started') || [];

        const combined = [...inProgressTasks, ...notStartedTasks, ...returnToHQ, ...inProgressStartTasks, ...notStartedStartTasks];
        const uniqueTasks = getUniqueByLatLng(combined);

        if (uniqueTasks.length > 0) {
          const taskCoords = uniqueTasks.map(task => [
            Number(task?.address?.longitude),
            Number(task?.address?.latitude)
          ]);

          const startPoint = [driverData.lng, driverData.lat];

          drawMultipleStraightLines(startPoint, taskCoords);

          // taskCoords.forEach(endPoint => {
          //   // fetchRoute(startPoint, endPoint);
          // });
        }
      });

      el.addEventListener('mouseleave', () => {
        removeRouteFromMap();
      });

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        showDriverInfo(driverData);
      });

      driverMarkersRef.current.push(marker);
    });
  }, [memoizeDriversLocation, driversLocation?.data]);

  const drawMultipleStraightLines = (startPoint, taskCoords) => {
    // Combine start point with the rest into one continuous line
    const allCoords = [startPoint, ...taskCoords];

    // Create line segments and arrow points
    const lineSegments = [];
    const arrowPoints = [];

    for (let i = 0; i < allCoords.length - 1; i++) {
      const start = allCoords[i];
      const end = allCoords[i + 1];

      // Add line segment
      lineSegments.push({
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: [start, end]
        }
      });

      // Calculate midpoint
      const midpoint = [
        (start[0] + end[0]) / 2,
        (start[1] + end[1]) / 2
      ];

      // Calculate bearing (direction) for the arrow
      const bearing = calculateBearing(
        { lng: start[0], lat: start[1] },
        { lng: end[0], lat: end[1] }
      );
      const rotationOffset = -210;

      // Add arrow point
      arrowPoints.push({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: midpoint
        },
        properties: {
          rotation: bearing + rotationOffset
        }
      });
    }

    const routeData = {
      type: "FeatureCollection",
      features: lineSegments
    };

    const arrowData = {
      type: "FeatureCollection",
      features: arrowPoints
    };

    addRouteToMapLine(routeData, arrowData);
  };

  // Calculate bearing between two points (in degrees)
  function calculateBearing(start, end) {
    const startLat = toRadians(start.lat);
    const startLng = toRadians(start.lng);
    const endLat = toRadians(end.lat);
    const endLng = toRadians(end.lng);

    const y = Math.sin(endLng - startLng) * Math.cos(endLat);
    const x = Math.cos(startLat) * Math.sin(endLat) -
      Math.sin(startLat) * Math.cos(endLat) * Math.cos(endLng - startLng);

    let bearing = toDegrees(Math.atan2(y, x));
    return (bearing + 360) % 360; // Normalize to 0-360
  }

  function toRadians(degrees) {
    return degrees * Math.PI / 180;
  }

  function toDegrees(radians) {
    return radians * 180 / Math.PI;
  }

  const addRouteToMapLine = (routeData, arrowData) => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    // Remove existing route if present
    removeRouteFromMap();

    // Add route source
    map.addSource('route', {
      type: 'geojson',
      data: routeData
    });

    // Add route line layer
    map.addLayer({
      id: 'route',
      type: 'line',
      source: 'route',
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': '#818183',
        'line-width': 4,
      },
    });

    // Load arrow icon and add arrows
    map.loadImage(arrowIcon, (error, image) => {
      if (error) {
        console.error('Error loading arrow icon:', error);
        return;
      }

      if (!map.hasImage('arrow-icon')) {
        map.addImage('arrow-icon', image);
      }

      // Add arrow source
      map.addSource('route-arrows', {
        type: 'geojson',
        data: arrowData
      });

      // Add arrow layer with rotation
      map.addLayer({
        id: 'route-arrows',
        type: 'symbol',
        source: 'route-arrows',
        layout: {
          'icon-image': 'arrow-icon',
          'icon-size': 0.3, // Adjust size as needed
          'icon-rotate': ['get', 'rotation'], // Use the rotation property we calculated
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true
        }
      });
    });
  };

  const showDriverInfo = useCallback((driverInfo) => {
    removePopup();
    const content = `
      <div class='tasks-modal map-driver-info'>
        <table class='hw-table' border='0' cellspacing='0' cellpadding='0'>
          <thead>
            <tr>
              <th>Driver Name</th>
              <th>Latitude</th>
              <th>Longitude</th>
          ${driverInfo?.driverTaskStatus === 'onBreak' ? '<th>Break Info</th>' : ''}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>${driverInfo?.driverName || '-'}</td>
              <td>${driverInfo?.lat || '-'}</td>
              <td>${driverInfo?.lng || '-'}</td>
             ${driverInfo?.driverTaskStatus === 'onBreak' ? `<td>${driverInfo?.taskName || '-'} (${driverInfo?.fromTime ? formatTimeTo12Hour(driverInfo?.fromTime) : '-'} - ${driverInfo?.toTime ? formatTimeTo12Hour(driverInfo?.toTime) : '-'})</td>` : '-'}       
                   </tr>
          </tbody>
        </table>
      </div>`;

    const popup = new mapboxgl.Popup()
      .setLngLat([driverInfo.lng, driverInfo.lat])
      .setHTML(content)
      .addTo(mapRef.current);

    // FIX: Set z-index to 5 (minimal) to stay below sidebar
    const popupEl = popup.getElement();
    if (popupEl) {
      popupEl.style.zIndex = '5';
    }

    popupRef.current = popup;
  }, []);

  const createTaskMarkerElement = (count, iconUrl) => {
    const el = document.createElement('div');
    el.style.position = 'relative';
    el.style.userSelect = 'none';
    el.style.cursor = 'pointer';

    const img = document.createElement('img');
    img.src = iconUrl;
    img.style.width = '22px';
    img.style.height = '22px';

    el.appendChild(img);

    if (count > 1) {
      const countEl = document.createElement('div');
      countEl.style.position = 'absolute';
      countEl.style.left = '50%';
      countEl.style.top = '4px';
      countEl.style.transform = 'translateX(-50%)';
      countEl.style.color = '#000000';
      countEl.style.borderRadius = '50%';
      countEl.style.fontSize = '10px';
      countEl.style.fontWeight = 'bold';
      countEl.style.textAlign = 'center';
      countEl.style.width = '12px';
      countEl.style.height = '12px';
      countEl.style.lineHeight = '12px';
      // countEl.style.backgroundColor = 'rgba(0,0,0,0.5)';
      countEl.textContent = count > 9 ? '9+' : count;

      el.appendChild(countEl);
    }

    return el;
  };

  const updateMarkers = useCallback(() => {
    const map = mapRef.current;
    if (!map || !map.getSource('task-locations')) return;

    const newMarkers = {};
    const features = map.queryRenderedFeatures({ layers: ['clusters', 'unclustered-point'] });

    // Group features by their coordinates to handle multiple tasks at the exact same location
    const groupedFeatures = new Map();
    features.forEach(feature => {
      const coordsKey = JSON.stringify(feature.geometry.coordinates);
      if (!groupedFeatures.has(coordsKey)) {
        groupedFeatures.set(coordsKey, []);
      }
      groupedFeatures.get(coordsKey).push(feature);
    });

    groupedFeatures.forEach((featuresAtLocation, coordsKey) => {
      const coords = featuresAtLocation[0].geometry.coordinates;
      const id = coordsKey; // Use coordinates as a unique ID for the location group

      newMarkers[id] = true; // Mark this location as having a marker

      if (!markersOnScreen.current[id]) {
        // Create new marker for this location group
        const el = document.createElement('div');
        el.className = 'task-marker-container';
        el.style.position = 'absolute';
        el.style.transform = 'translate(-50%, -50%)';
        el.style.willChange = 'transform';
        el.style.zIndex = '1';

        let markerNode;
        let count = 0;

        // Deduplicate features to avoid double counting if Mapbox returns duplicates
        const uniqueFeatures = [];
        const seenIds = new Set();
        featuresAtLocation.forEach(f => {
          const fId = f.properties.id || f.properties.cluster_id; // Use cluster_id for clusters
          if (!seenIds.has(fId)) {
            seenIds.add(fId);
            uniqueFeatures.push(f);
          }
        });

        // Determine if it's a cluster or individual tasks
        const isCluster = uniqueFeatures.some(f => f.properties.cluster);

        if (isCluster) {
          // If any feature at this location is a cluster, treat the whole group as a cluster
          const clusterFeature = uniqueFeatures.find(f => f.properties.cluster);
          // Use the aggregated point_count_sum if available (for stacked tasks), otherwise fall back to point_count
          count = clusterFeature.properties.point_count_sum || clusterFeature.properties.point_count;
          // Use notStartedMarker (yellow) for clusters as requested
          markerNode = createTaskMarkerElement(count, notStartedMarker);
          el.appendChild(markerNode);
          el.style.zIndex = '3'; // Level 3: Clusters

          el.addEventListener('click', (e) => {
            e.stopPropagation();
            const clusterId = clusterFeature.properties.cluster_id;
            map.getSource('task-locations').getClusterLeaves(
              clusterId,
              Infinity, // Get all leaves
              0,
              (err, aFeatures) => {
                if (err) return;
                // Accumulate tasks from leaves. 
                // Leaves can be manual stacks (with multiple tasks) or singles.
                const allTasks = [];
                aFeatures.forEach(leaf => {
                  const leafTasks = taskDataMapRef.current[leaf.properties.originalTaskId];
                  if (leafTasks) {
                    allTasks.push(...leafTasks);
                  }
                });
                showTaskInfo(allTasks, coords);
              }
            );
          });
        } else {
          // Not a cluster, these are individual tasks (or manual stacks) at the same location
          // Flatten the array of arrays to get all individual tasks
          const tasksAtLocation = uniqueFeatures
            .map(f => taskDataMapRef.current[f.properties.originalTaskId])
            .filter(Boolean)
            .flat();

          count = tasksAtLocation.length;

          // If multiple tasks at the same exact spot, show a count.
          // Otherwise, show a single task icon.
          if (count > 1) {
            markerNode = createTaskMarkerElement(count, notStartedMarker); // Use yellow for grouped tasks
            el.style.zIndex = '2'; // Lowered from 5 to 2
          } else {
            const task = tasksAtLocation[0];
            const iconUrl = ((task?.fleetName === undefined || task?.fleetName === 'Unassigned') && task?.orderType === 'asap') ? flagIcon : getIconbyStatus(task.taskType !== 'returnToHeadquarterTask' ? task.status : task.taskType);
            markerNode = createTaskMarkerElement(1, iconUrl);
          }
          el.appendChild(markerNode);

          el.addEventListener('click', (e) => {
            e.stopPropagation();
            showTaskInfo(tasksAtLocation, coords);
          });
        }

        const marker = new mapboxgl.Marker({
          element: el,
          anchor: 'center'
        })
          .setLngLat(coords)
          .addTo(map);

        markersOnScreen.current[id] = marker;
      }
    });

    // Remove markers that are no longer on screen
    for (const id in markersOnScreen.current) {
      if (!newMarkers[id]) {
        markersOnScreen.current[id].remove();
        delete markersOnScreen.current[id];
      }
    }
  }, []);

  const showTaskLocations = useCallback(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    // Clear previous task data from ref
    taskDataMapRef.current = {};

    // Group tasks by EXACT location manually first
    const locationMap = new Map();
    memoizeDriversTasksLocation.forEach((task) => {
      const lat = Number(task?.lat);
      const lng = Number(task?.lng);
      if (isNaN(lat) || isNaN(lng)) return;

      // Exact grouping
      const key = `${lat},${lng}`;
      if (!locationMap.has(key)) {
        locationMap.set(key, []);
      }
      locationMap.get(key).push(task);
    });

    const features = [];

    Array.from(locationMap.entries()).forEach(([key, tasks], index) => {
      const uniqueId = `group-${index}`;
      taskDataMapRef.current[uniqueId] = tasks;

      const firstTask = tasks[0];
      const lat = Number(firstTask.lat);
      const lng = Number(firstTask.lng);

      features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [lng, lat]
        },
        properties: {
          id: uniqueId, // Unique ID for the feature
          originalTaskId: uniqueId, // Reference to the full task object in taskDataMapRef
          count: tasks.length, // This is key for clusterProperties to sum
          // Add other properties if needed for direct filtering/styling in Mapbox GL JS
          status: firstTask.status,
          taskType: firstTask.taskType,
          fleetName: firstTask.fleetName,
          orderType: firstTask.orderType
        }
      });
    });

    const geoJsonObject = {
      type: 'FeatureCollection',
      features: features
    };

    const source = map.getSource('task-locations');
    if (source) {
      source.setData(geoJsonObject);
    }
  }, [memoizeDriversTasksLocation]);


  const showTaskInfo = useCallback((tasks, lngLat) => {
    removePopup();

    const generateTableContent = (tasks) => {
      return tasks.map(task => {
        const { recipientDetails = {}, taskType = '', status = '', address = {}, fleet = {}, taskName = '', taskStartTime, taskEndTime, actualTaskEndTime, actualTaskStartTime, orderType, fleetName } = task || {};

        const taskAddress = address?.address1 || '-';
        let orderTypeDisplay = '-';
        if (orderType) {
          orderTypeDisplay = orderType === 'asap'
            ? orderType.toUpperCase()  // "ASAP"
            : orderType.charAt(0).toUpperCase() + orderType.slice(1); // 首字母大写
        }
        return `
          <tr data-task-id='${task?._id}'data-task-info='${encodeURIComponent(JSON.stringify(task))}' data-destination='${task?._id}' class='clickable-row' >
            <td>
               <div class='w-100 ellipsis-one d-flex align-items-center' style='max-width: 200px'>
                <img class='mr-2'  src=${((fleetName === undefined || fleetName === 'Unassigned') && orderType === 'asap') ? flagIcon : getIconbyStatus(taskType !== 'returnToHeadquarterTask' ? status : taskType)}  width='16' height='16'/> 
             ${task?.taskDisplayId || '-'}
              </div>
              <div></td>
            <td>
              <div class='w-100 ellipsis-one d-flex align-items-center' style='max-width: 200px'>
                <span class='text-truncate'>${taskAddress}</span>
              </div>
            </td>
            <td>${(taskType === 'breakTask' || taskType === 'returnToHeadquarterTask') ? taskName : taskType === 'startTask' ? 'Start Task' : recipientDetails?.name || '-'}</td>      
            <td>${fleet?.fleetName || '-'}</td>
            <td>
              ${taskType === 'breakTask' ?
            `${task?.fromTime ? formatTimeTo12Hour(task?.fromTime) : '-'} - ${task?.toTime ? formatTimeTo12Hour(task?.toTime) : '-'}` :
            status === 'completed' ?
              `${actualTaskStartTime ? formatTimestampTo12HourTimeUtc(actualTaskStartTime) : '-'} - ${actualTaskEndTime ? formatTimestampTo12HourTimeUtc(actualTaskEndTime) : '-'}` :
              status === 'not_started' ? '-' :
                `${actualTaskStartTime ? formatTimestampTo12HourTimeUtc(actualTaskStartTime) : '-'} - ${status !== 'in_progress' ? (actualTaskEndTime ? formatTimestampTo12HourTimeUtc(actualTaskEndTime) : '-') : '-'}`}
            </td>
              <td>${orderType ? orderTypeDisplay : ''}  
              ${orderType !== 'asap' ? `${formatTimeTo12Hour(task?.slotDetails?.startTime)} - ${formatTimeTo12Hour(task?.slotDetails?.endTime)}` : ''}
              </td>    
              <td> ${task?.coreRegion?.coreRegionName || '-'}</td>    

          </tr>`;
      }).join('');
    };

    const content = `
      <div class='tasks-modal map-task-info'>
        <Typography variant='h6' class='w-100 d-flex font-w-600 text-black-400 text-14 text-left px-3 py-2'>${tasks?.length || 0}  Tasks</Typography>
        <table class='hw-table' border='0' cellspacing='0' cellpadding='0'>
          <thead>
            <tr>
              <th>Task Id</th>
              <th>Destination</th>
              <th>Recipient</th>
              <th>Driver</th>              
              <th>Date/Time</th>
              <th>Delivery Speed</th>
              <th>Core Region</th>
            </tr>
          </thead>
          <tbody>
            ${generateTableContent(tasks)}
          </tbody>
        </table>
      </div>`;

    const popup = new mapboxgl.Popup({
      closeButton: true, // Ensure close button is enabled
      closeOnClick: true,
      maxWidth: '600px',
      className: 'task-info-popup'
    })
      .setLngLat(lngLat)
      .setHTML(content)
      .addTo(mapRef.current);

    // FIX: Set z-index to 5 (minimal) to stay below sidebar
    const popupEl = popup.getElement();
    if (popupEl) {
      popupEl.style.zIndex = '5';
    }

    popupRef.current = popup;
    // Add click event listeners to each row after the popup is added to DOM
    setTimeout(() => {
      const rows = document.querySelectorAll('.clickable-row');
      rows.forEach(row => {
        let clickTimer = null;
        let isDoubleClick = false;

        // Handle single click on row
        row.addEventListener('click', (e) => {
          // Check if the click was on the view icon
          if (e.target.classList.contains('view-icon') ||
            e.target.closest('.view-icon')) {
            return; // Don't trigger row click for view icon clicks
          }

          // Check if this is a double click
          if (isDoubleClick) {
            isDoubleClick = false;
            return;
          }

          // Set timer for single click
          clickTimer = setTimeout(() => {
            // Get the task info from data attribute
            const taskInfo = row.getAttribute('data-task-info');
            if (taskInfo) {
              const taskId = row.getAttribute('data-task-id');
              handleTaskClick(taskId);
            }
          }, 250); // 250ms delay to detect double click
        });

        // Handle double click on row
        row.addEventListener('dblclick', (e) => {
          // Clear the single click timer
          clearTimeout(clickTimer);
          isDoubleClick = true;

          // Don't trigger for view icon
          if (e.target.classList.contains('view-icon') ||
            e.target.closest('.view-icon')) {
            return;
          }

          const taskInfo = row.getAttribute('data-task-info');
          if (taskInfo) {
            const taskId = row.getAttribute('data-task-id');
            handleViewIconClick(taskId);
          }
        });



        // Add hover effect for better UX
        row.style.cursor = 'pointer';
        row.addEventListener('mouseenter', () => {
          row.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
        });
        row.addEventListener('mouseleave', () => {
          row.style.backgroundColor = '';
        });

      });

    }, 100); // Small delay to ensure DOM is ready

  }, []);

  const fetchDriverTasksLists = useCallback(() => {
    if (!dataPassComponent?.endDate && !dataPassComponent?.startDate) return;

    dispatch(fetchAllDriversLocations({
      startDate: dataPassComponent?.startDate ? formatTimestampToMapOld(dataPassComponent?.startDate) : '',
      endDate: dataPassComponent?.endDate ? formatTimestampToMapOld(dataPassComponent?.endDate) : '',
      taskOrderDistance: 'asc',
      taskStatus: dataPassComponent?.taskStatus || 'not_started,in_progress,unassigned,completed,cancelled',
    }));
  }, [dataPassComponent, dispatch]);

  const fetchDriversLocationList = useCallback(() => {
    dispatch(fetchLiveDriversLocations({
      driverStatus: dataPassComponent?.driverStatus || 'intransit,idle,onbreak,outOfService',
    }));
  }, [dataPassComponent, dispatch]);

  // Initial data fetch
  useEffect(() => {
    fetchDriverTasksLists();
    fetchDriversLocationList();
  }, [fetchDriverTasksLists, fetchDriversLocationList]);

  // Set up live location polling
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isFetchingLiveDrivers) {
        fetchDriversLocationList();
        fetchDriverTasksLists()
      }
      // }, 1000); 
    }, 20000);

    return () => clearInterval(interval);
  }, [fetchDriversLocationList, isFetchingLiveDrivers]);

  // Show driver locations when data changes
  useEffect(() => {
    if (isFetchingLiveDrivers) return;
    showDriverLocations();

  }, [isFetchingLiveDrivers, showDriverLocations]);

  // Show task locations when data changes
  useEffect(() => {
    if (loading) return;
    showTaskLocations();
  }, [loading, memoizeDriversTasksLocation, showTaskLocations]);

  // Open task detail drawer when task is selected
  useEffect(() => {
    if (!taskDetailFetch) return;

    dispatch(openCustomDrawer({
      drawer: 'TASK_DETAIL',
      drawerData: taskDetailFetch
    }));
    setTaskDetailFetch('');
  }, [taskDetailFetch, dispatch]);

  const updateDriverLocation = ({ lat, lng, driver }) => {
    const payload = {
      "fleetId": driver?.fleetId,
      "lat": Number(lat),
      "long": Number(lng),
    }
    dispatch(updateDriverStatusApi(payload))
      .unwrap()
      .then(res => {
        fetchDriversLocationList();
        fetchDriverTasksLists()
        // showResponseMessage(res?.message, 'success');
      })
      .catch(err => {
        // showResponseMessage(err?.message, 'error');
      });
  }

  // useEffect(() => {
  //   dispatch(getKmlData())
  // }, [])

  const handleViewIconClick = (taskId) => {
    dispatch(openCustomDrawer({
      drawer: 'TASK_DETAIL_MAP',
      drawerData: taskId,
      data: { handlerefresh },
      activeScreen: "drawer-map",
    }))
  }
  const handlerefresh = () => {
    
  }
  // useEffect(() => {
  //     dispatch(getKmlData())
  // }, [])
  return (
    <>  {
      testingPanel ? <div
        ref={mapContainerRef}
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: '#0F0F13',
          paddingLeft: '16px',
          paddingBottom: '16px'
        }}
      />
        :

        <div className='hd-left-wrapper'>

          <div
            ref={mapContainerRef}
            style={{
              width: '100%',
              height: '100%',
              backgroundColor: '#0F0F13',
              paddingLeft: '16px',
              paddingBottom: '16px'
            }}
          />
        </div>
    }
    </>
  );
};
export default React.memo(MapDrive);